import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { firstValueFrom, lastValueFrom, take, takeUntil, tap } from 'rxjs';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { Product, ProductionOrderOperation, ProductionOrderPlanner, TId, Workcenter } from 'src/app/models';
import { OmsManufacturingOrdersEditComponent } from '../../../oms/production/oms-manufacturing-orders/oms-manufacturing-orders-edit/oms-manufacturing-orders-edit.component';
import { LangService } from 'src/app/services/lang.service';
import { MasterApiService } from 'src/app/services/master-api.service';

type TOperationListItem = {
  id: TId;
  name: string;
  workcenters: string;
  duration: string;
  start: Date;
  end: Date;
  dependencies: TId[];
}

@Component({
    selector: 'app-planner-detail',
    templateUrl: './planner-detail.component.html',
    styleUrl: './planner-detail.component.sass',
    standalone: false
})
export class PlannerDetailComponent implements OnChanges {
  @Input() operationId?: TId;  // If passed, it just highlights the selected operation.
  @Input() workcenters?: Workcenter[];  @Input() startDate?: Date;
  @Input() endDate?: Date;

  @Input() productionOrder?: ProductionOrderPlanner;
  @Output() productionOrderChange: EventEmitter<ProductionOrderPlanner> = new EventEmitter<ProductionOrderPlanner>();

  @Output() showFullScreen: EventEmitter<boolean> = new EventEmitter<boolean>();

  OFProduct?: Product;
  OFCoProducts: Product[] = [];
  OFByProducts: Product[] = [];
  products: Product[] = [];
  operations: ProductionOrderOperation[] = [];
  operationsList: TOperationListItem[] = [];

  fullscreen: boolean = false;
  langCode$ = this.lang.code$;

  constructor(private api: MasterApiService, private tabSrv: TabsService, private lang: LangService) { }

  async ngOnChanges(): Promise<void> {
    if (this.workcenters === undefined) {
      this.workcenters = await lastValueFrom(this.api.resources.workcenters.getAll());
    }
    const  operationsRq = this.api.resources.operations.productionOrderOperations(this.productionOrder!.id).getAll().pipe(
      tap(operations => {
        // Sort by dependencies
        const sorted: ProductionOrderOperation[] = operations.filter(o => o.dependencies.length === 0);
        let deps = operations.filter(o => o.dependencies.length > 0);
        while (deps.length > 0) {
          const next = deps.filter(o => o.dependencies.every(d => sorted.some(s => s.id === d.id)));
          if (next.length === 0) {
            throw new Error('Circular dependency');
          }
          sorted.push(...next);
          deps = deps.filter(o => !next.some(n => n.id === o.id));
        }
        this.operationsList = sorted.map(operation => {
          const workCenters = operation.workCenters.map(wc => this.workcenters!.find(w => w.id === wc)!.name.trim());
          return {
            id: operation.id,
            name: operation.name,
            workcenters: workCenters.length === 1 ? workCenters[0] : `[${workCenters.join(', ')}]`,
            duration:  operation.duration,
            start: operation.plannedStartDate,
            end: operation.plannedEndDate,
            dependencies: operation.dependencies.map(d => d.id),
          };
        });


      }),
    );
    if (this.productionOrder !== undefined) {
      this.operations = await firstValueFrom(operationsRq);
      const product = this.productionOrder.products.find(product => product.typeId === 1 && product.coProductTypeId === 1);
      if (product !== undefined) {
        const productId = product.productId;
        (this.productionOrder as any).quantity = product.quantity;

        this.OFProduct = await lastValueFrom(this.api.resources.product.products.get(productId));
        if (this.OFProduct !== undefined) {
          const productFormat = await lastValueFrom(this.api.resources.product.productFormats(this.OFProduct.id!).get(this.OFProduct.stockFormatId!));
          if (productFormat !== undefined)
            this.OFProduct.stockFormatCode = productFormat.formatCode;
          (this.productionOrder as any).product = this.OFProduct;
        }
      }

      const coProducts = this.productionOrder.products.filter(product => product.typeId === 1 && product.coProductTypeId === 2);
      for (const coProduct of coProducts) {
        const product = await lastValueFrom(this.api.resources.product.products.get(coProduct.productId));
        if (product !== undefined) {
          const productFormat = await lastValueFrom(this.api.resources.product.productFormats(product.id!).get(product.stockFormatId!));
          if (productFormat !== undefined)
            product.stockFormatCode = coProduct.quantity + ' ' + productFormat.formatCode;
          this.OFCoProducts.push(product);
        }
      }

      const byProducts = this.productionOrder.products.filter(product => product.typeId === 1 && product.coProductTypeId === 3);
      for (const byProduct of byProducts) {
        const product = await lastValueFrom(this.api.resources.product.products.get(byProduct.productId));
        if (product !== undefined) {
          const productFormat = await lastValueFrom(this.api.resources.product.productFormats(product.id!).get(product.stockFormatId!));
          if (productFormat !== undefined)
            product.stockFormatCode =  byProduct.quantity + ' ' + productFormat.formatCode;
          this.OFByProducts.push(product);
        }
      }
    }

    // this.chartMenus = [];
    await this.loadProducts();
    // await this.loadStockEntries();
    // await this.loadOperatorsOcupation();
    // await this.loadEquipmentCategories();
    // await this.loadDocumentPendingBatches();

    // const stockCharts = await this.generateStockChart();
    // const centerCharts = await this.generateWorkCentersChart();
    // const operatorCharts = await this.generateWorkforcesChart();
    // const equipmentCharts = await this.generateEquipmentChart();

    // this.chartMenus.push(stockCharts, centerCharts, operatorCharts, equipmentCharts);
  }

  async editProductionOrder(productionOrderId: TId): Promise<void> {
    const tab = this.tabSrv.createTab(
      OmsManufacturingOrdersEditComponent,
      TabRole.EDITION,
      { inputPrms: { id: productionOrderId } }
    );
    tab.component.instance.entityChanged.pipe(
      takeUntil(tab.destroy$),
      take(1)
    ).subscribe(productionOrderEdited => {
      tab.close();
      this.productionOrderChange.emit(productionOrderEdited as any);
      // this.updateProductionOrderEdited(productionOrderEdited)
    } );
  }

  close(): void {
    this.productionOrder = undefined;
    this.productionOrderChange.emit(this.productionOrder);
    this.showFullScreen.emit(false);
  }

  changeFullscreen(): void {
    this.fullscreen = !this.fullscreen;
    this.showFullScreen.emit(this.fullscreen);
  }

    //LOAD
    private async loadProducts(): Promise<void> {
      this.products = [];

      // if (this.OFProduct !== undefined) {
      //   this.products.push(this.OFProduct);
      // }

      const productsPromises = this.productionOrder?.products?.map(async productionOrderProduct => {
        if (this.products.some(item => item.id === productionOrderProduct.productId)) {
          return;
        }

        const product: Product = await lastValueFrom(this.api.resources.product.products.get(productionOrderProduct.productId!));
        const productFormat = await lastValueFrom(this.api.resources.product.productFormats(productionOrderProduct.productId!).get(productionOrderProduct.productFormatId!));
        product.stockFormatCode = productFormat.formatCode;
        if (!this.products.find(item => product!.id === item.id)) {
          this.products.push(product);
        }
      });

      if (productsPromises) {
        await Promise.all(productsPromises);
      }

      const materialsPromises = this.productionOrder?.materials?.map(async productionOrderMaterial => {
        if (this.products.some(item => item.id === productionOrderMaterial.productId)) {
          return;
        }

        const product: Product = await lastValueFrom(this.api.resources.product.products.get(productionOrderMaterial.productId!));
        const productFormat = await lastValueFrom(this.api.resources.product.productFormats(productionOrderMaterial.productId!).get(productionOrderMaterial.productFormatId!));
        product.stockFormatCode = productFormat.formatCode;
        if (!this.products.find(item => product!.id === item.id)) {
          this.products.push(product);
        }
      });

      if (materialsPromises) {
        await Promise.all(materialsPromises);
      }
    }
}
